import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { type SEOHandle } from '@nasa-gcn/remix-seo'
import {
	data,
	redirect,
	type ActionFunctionArgs,
	type LinksFunction,
	type LoaderFunctionArgs,
	type MetaFunction,
	Form,
	Link,
	useActionData,
	useLoaderData,
	useSearchParams,
} from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { ErrorList, HouseMark } from '#app/components/atoms'
import { StatusButton } from '#app/components/molecules'
import { CheckboxField, Field } from '#app/components/molecules/form'
import { GeneralErrorBoundary } from '#app/components/templates'
import { login, requireAnonymous } from '#app/utils/auth.server.ts'
import {
	ProviderConnectionForm,
	type ProviderName,
} from '#app/utils/connections.tsx'
import { getEnabledProviderNames } from '#app/utils/connections.server.ts'
import { HELP_CONTACT } from '#app/utils/help-contact.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { useIsPending } from '#app/utils/misc.tsx'
import { redirectWithToast } from '#app/utils/toast.server.ts'
import {
	EmailSchema,
	PasswordSchema,
	UsernameSchema,
} from '#app/utils/user-validation.ts'
import authStyleUrl from '../../styles/auth.css?url'
import { sendLoginCode } from './login-code.server.tsx'
import { handleNewSession } from './login.server.ts'
import { pageMeta } from '#app/utils/page-meta.ts'

export const handle: SEOHandle = {
	getSitemapEntries: () => null,
}

export const links: LinksFunction = () => [
	{ rel: 'stylesheet', href: authStyleUrl },
]

const CodeFormSchema = z.object({
	email: EmailSchema,
	redirectTo: z.string().optional(),
})

const LoginFormSchema = z.object({
	username: UsernameSchema,
	password: PasswordSchema,
	redirectTo: z.string().optional(),
	remember: z.boolean().optional(),
})

export async function loader({ request }: LoaderFunctionArgs) {
	await requireAnonymous(request)
	return data({ enabledProviders: getEnabledProviderNames() })
}

export async function action({ request }: ActionFunctionArgs) {
	await requireAnonymous(request)
	const formData = await request.formData()
	checkHoneypot(formData)

	if (formData.get('intent') === 'code') {
		const submission = parseWithZod(formData, { schema: CodeFormSchema })
		if (submission.status !== 'success') {
			return data(
				{ intent: 'code' as const, result: submission.reply() },
				{ status: 400 },
			)
		}
		const { email, redirectTo } = submission.value
		const sent = await sendLoginCode({ request, email, redirectTo })
		if (sent.status === 'error') {
			return data(
				{
					intent: 'code' as const,
					result: submission.reply({
						formErrors: [
							'Vi fekk ikkje sendt e-posten akkurat no. Prøv igjen om litt.',
						],
					}),
				},
				{ status: 500 },
			)
		}
		const codePage = `${sent.codePage.pathname}${sent.codePage.search}`
		if (sent.status === 'recent') {
			throw await redirectWithToast(codePage, {
				title: 'Koden er på veg',
				description:
					'Vi sende nettopp ein kode. Sjekk innboksen — og søppelposten.',
			})
		}
		return redirect(codePage)
	}

	const submission = await parseWithZod(formData, {
		schema: (intent) =>
			LoginFormSchema.transform(async (data, ctx) => {
				if (intent !== null) return { ...data, session: null }

				const session = await login(data)
				if (!session) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'Ugyldig brukarnamn eller passord',
					})
					return z.NEVER
				}

				return { ...data, session }
			}),
		async: true,
	})

	if (submission.status !== 'success' || !submission.value.session) {
		return data(
			{
				intent: 'password' as const,
				result: submission.reply({ hideFields: ['password'] }),
			},
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}

	const { session, remember, redirectTo } = submission.value

	return handleNewSession({
		request,
		session,
		remember: remember ?? false,
		redirectTo,
	})
}

/** on the way to buy tickets? say so */
const isBuying = (redirectTo: string | null) =>
	Boolean(redirectTo && /^\/(events|tickets)\//.test(redirectTo))

export default function LoginPage() {
	const actionData = useActionData<typeof action>()
	const isPending = useIsPending()
	const [searchParams] = useSearchParams()
	const redirectTo = searchParams.get('redirectTo')
	const enabledProviders = useLoaderData<typeof loader>()
		.enabledProviders as Array<ProviderName>

	const [codeForm, codeFields] = useForm({
		id: 'login-code-form',
		constraint: getZodConstraint(CodeFormSchema),
		defaultValue: { redirectTo, email: searchParams.get('email') },
		lastResult: actionData?.intent === 'code' ? actionData.result : undefined,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: CodeFormSchema })
		},
		shouldRevalidate: 'onBlur',
	})

	const [form, fields] = useForm({
		id: 'login-form',
		constraint: getZodConstraint(LoginFormSchema),
		defaultValue: { redirectTo },
		lastResult:
			actionData?.intent === 'password' ? actionData.result : undefined,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: LoginFormSchema })
		},
		shouldRevalidate: 'onBlur',
	})
	const passwordTried = actionData?.intent === 'password'

	return (
		<main className="au">
			<div className="au-card">
				<div className="au-mark">
					<HouseMark />
				</div>
				<p className="au-kicker">
					{isBuying(redirectTo) ? 'Eitt steg igjen' : 'Velkomen'}
				</p>
				<h1 className="au-title">Logg inn</h1>
				<p className="au-lede">
					Skriv inn e-posten din, så sender vi deg ein kode. Er du ny, lagar vi
					ein brukar til deg på vegen — utan passord å hugse.
				</p>

				<Form method="POST" className="au-form" {...getFormProps(codeForm)}>
					<HoneypotInputs />
					<input type="hidden" name="intent" value="code" />
					<input
						{...getInputProps(codeFields.redirectTo, { type: 'hidden' })}
					/>
					<label htmlFor={codeFields.email.id} className="au-label">
						E-post
					</label>
					<input
						{...getInputProps(codeFields.email, { type: 'email' })}
						className="au-input"
						autoComplete="email"
						inputMode="email"
						autoCapitalize="none"
						spellCheck={false}
						placeholder="namn@eksempel.no"
						autoFocus
					/>
					<ErrorList
						id={codeFields.email.errorId}
						errors={codeFields.email.errors}
					/>
					<ErrorList id={codeForm.errorId} errors={codeForm.errors} />
					<StatusButton
						className="au-submit"
						status={isPending ? 'pending' : (codeForm.status ?? 'idle')}
						type="submit"
						disabled={isPending}
					>
						Send kode
					</StatusButton>
				</Form>

				{enabledProviders.length > 0 ? (
					<>
						<p className="au-or">
							<span>eller</span>
						</p>
						<ul className="au-providers">
							{enabledProviders.map((providerName) => (
								<li key={providerName}>
									<ProviderConnectionForm
										type="Login"
										providerName={providerName}
										redirectTo={redirectTo}
									/>
								</li>
							))}
						</ul>
					</>
				) : null}

				<details className="au-password" open={passwordTried || undefined}>
					<summary>Logg inn med brukarnamn og passord</summary>
					<Form
						method="POST"
						className="au-password-form"
						{...getFormProps(form)}
					>
						<HoneypotInputs />
						<input type="hidden" name="intent" value="password" />
						<Field
							labelProps={{ children: 'Brukarnamn' }}
							inputProps={{
								...getInputProps(fields.username, { type: 'text' }),
								className: 'lowercase',
								autoComplete: 'username',
								autoCapitalize: 'none',
							}}
							errors={fields.username.errors}
						/>
						<Field
							labelProps={{ children: 'Passord' }}
							inputProps={{
								...getInputProps(fields.password, { type: 'password' }),
								autoComplete: 'current-password',
							}}
							errors={fields.password.errors}
						/>
						<div className="flex items-center justify-between gap-4">
							<CheckboxField
								labelProps={{
									htmlFor: fields.remember.id,
									children: 'Hugs meg',
								}}
								buttonProps={getInputProps(fields.remember, {
									type: 'checkbox',
								})}
								errors={fields.remember.errors}
							/>
							<Link to="/forgot-password" className="au-link">
								Gløymt passord?
							</Link>
						</div>
						<input {...getInputProps(fields.redirectTo, { type: 'hidden' })} />
						<ErrorList errors={form.errors} id={form.errorId} />
						<StatusButton
							className="au-submit au-submit-quiet"
							status={isPending ? 'pending' : (form.status ?? 'idle')}
							type="submit"
							disabled={isPending}
						>
							Logg inn
						</StatusButton>
					</Form>
				</details>
			</div>

			<aside className="au-help" aria-labelledby="au-help-title">
				<h2 id="au-help-title" className="au-help-title">
					Treng du hjelp?
				</h2>
				<p>{HELP_CONTACT.name} hjelper deg gjerne med å reservere billettar:</p>
				<ul>
					<li>
						<a href={`tel:${HELP_CONTACT.tel}`}>Ring {HELP_CONTACT.phone}</a>
					</li>
					<li>
						<a
							href={`mailto:${HELP_CONTACT.email}?subject=Hjelp%20Huskonsertfestivalen`}
						>
							{HELP_CONTACT.email}
						</a>
					</li>
				</ul>
			</aside>
		</main>
	)
}

export const meta: MetaFunction = (args) =>
	pageMeta(args, { title: 'Logg inn' })

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
